import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ContractWithUsers, ContractViewMode, ContractStatus } from '../../types/contract';
import { StatusBadge } from './StatusBadge';
import {
  contractStatusConfig,
  formatCurrency,
  formatDate,
  getDaysUntil,
  isOverdue,
  getUserRole,
  canEditContract,
  canCancelContract,
} from '../../utils/contractHelpers';
import { ArrowRight, Calendar, AlertCircle, MoreVertical, Flag, MessageSquare, Edit, Send, Trash2 } from 'lucide-react';
import { DisputeModal } from './DisputeModal';
import disputeService from '../../services/disputeService';
import { useToast } from '../../components/UI/Toast';
import { contractService } from '../../services/contractService';

interface ContractCardProps {
  contract: ContractWithUsers;
  viewMode: ContractViewMode;
  currentUserId: string;
}

// Extend status type locally to support UI-only 'DISPUTED' state
type DisplayStatus = ContractStatus | 'DISPUTED';

export const ContractCard: React.FC<ContractCardProps> = ({
  contract,
  viewMode,
  currentUserId,
}) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const role = getUserRole(contract, currentUserId);
  const otherParty = viewMode === 'client' ? contract.creator : contract.client;

  const [isDisputeOpen, setIsDisputeOpen] = useState(false);
  // Local state to simulate status change
  const [currentStatus, setCurrentStatus] = useState<DisplayStatus>(contract.status);

  const daysUntilDeadline = getDaysUntil(contract.estimated_delivery_date);
  const isDeadlineOverdue = isOverdue(contract.estimated_delivery_date);

  const handleClick = (e: React.MouseEvent) => {
    // Prevent navigation if clicking on dropdown or interactive elements
    if ((e.target as HTMLElement).closest('.stop-propagation')) {
      e.stopPropagation();
      return;
    }
    navigate(`/contracts/${contract.contract_id}?view=${viewMode}`);
  };

  const handleDisputeSubmit = async (data: any) => {
    try {
      const res = await disputeService.create({
        contractId: contract.contract_id,
        reason: data.reason,
        expectation: data.expectation,
        description: data.description,
      });
      if (res.success) {
        setIsDisputeOpen(false);
        setCurrentStatus('DISPUTED');
        showToast('申诉申请已提交，平台客服将尽快介入', 'success');
      } else {
        showToast((res as any).error || '提交失败，请重试', 'error');
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || '提交失败，请重试', 'error');
    }
  };

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const toggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
  };

  return (
    <>
      <div
        className="group bg-white rounded-2xl border border-slate-100 overflow-visible transition-all duration-300 hover:shadow-xl hover:border-blue-100 hover:-translate-y-1 cursor-pointer flex flex-col h-full relative"
        onClick={handleClick}
        onMouseLeave={() => setIsDropdownOpen(false)}
      >
        {/* Card Header */}
        <div className="p-6 border-b border-slate-50 relative">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 pr-6">
              <div className="flex items-center gap-2 mb-2">
                <StatusBadge
                  config={currentStatus === 'DISPUTED'
                    ? { label: '申诉中', color: 'text-red-700', bgColor: 'bg-red-100 border-red-200' }
                    : contractStatusConfig[currentStatus as ContractStatus] || contractStatusConfig[contract.status]}
                  size="sm"
                />
                {contract.brief_id && (
                  <span className="text-xs text-slate-500 bg-slate-50 px-2 py-0.5 rounded">
                    Brief #{contract.brief_id.slice(-6)}
                  </span>
                )}
              </div>
              <h3 className="text-lg font-semibold text-slate-900 group-hover:text-blue-600 transition-colors line-clamp-2">
                {contract.title}
              </h3>
            </div>

            {/* Options Menu */}
            <div className="absolute top-4 right-4 stop-propagation">
              <button
                onClick={toggleDropdown}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                aria-label="更多操作"
              >
                <MoreVertical size={20} />
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-100 rounded-xl shadow-lg py-1 z-20 animate-in fade-in zoom-in-95 duration-200">
                  <button
                    onClick={() => {
                      setIsDisputeOpen(true);
                      setIsDropdownOpen(false);
                    }}
                    className="w-full px-4 py-2.5 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2 transition-colors"
                  >
                    <Flag size={14} className="text-amber-500" />
                    <span>申请售后/申诉</span>
                  </button>
                  <button
                    onClick={() => showToast('正在连接客服...', 'info')}
                    className="w-full px-4 py-2.5 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2 transition-colors"
                  >
                    <MessageSquare size={14} className="text-blue-500" />
                    <span>联系人工客服</span>
                  </button>

                  {contract.status === 'DRAFT' && role === 'creator' ? (
                    <>
                      <button
                        onClick={() => navigate(`/contracts/${contract.contract_id}/edit?view=${viewMode}`)}
                        className="w-full px-4 py-2.5 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2 transition-colors border-t border-slate-50 mt-1 pt-2"
                      >
                        <Edit size={14} className="text-slate-500" />
                        <span>编辑合同</span>
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const res = await contractService.publishContract(contract.contract_id);
                            if (res.success) {
                              showToast('合同已提交审核', 'success');
                              setCurrentStatus('PENDING_CLIENT_APPROVAL');
                            } else {
                              showToast(res.error || '提交失败', 'error');
                            }
                          } catch (error) {
                            showToast('提交失败', 'error');
                          }
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2 transition-colors"
                      >
                        <Send size={14} className="text-green-500" />
                        <span>提交审核</span>
                      </button>
                      <button
                        onClick={async () => {
                          if (window.confirm('确定要删除草稿吗？此操作无法撤销。')) {
                            try {
                              const res = await contractService.deleteContract(contract.contract_id);
                              if (res.success) {
                                showToast('草稿已删除', 'success');
                                window.location.reload();
                              } else {
                                showToast(res.error || '删除失败', 'error');
                              }
                            } catch (error) {
                              showToast('删除失败', 'error');
                            }
                          }
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                      >
                        <Trash2 size={14} className="text-red-500" />
                        <span>删除草稿</span>
                      </button>
                    </>
                  ) : (
                    <>
                      {canEditContract(contract, currentUserId) && (
                        <button
                          onClick={() => navigate(`/contracts/${contract.contract_id}/edit?view=${viewMode}`)}
                          className="w-full px-4 py-2.5 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2 transition-colors border-t border-slate-50 mt-1 pt-2"
                        >
                          <Edit size={14} className="text-slate-500" />
                          <span>编辑合同</span>
                        </button>
                      )}
                      {canCancelContract(contract, currentUserId) && (
                        <button
                          onClick={async () => {
                            if (window.confirm('确定要取消合同吗？')) {
                              try {
                                const res = await contractService.cancelContract(contract.contract_id, 'Cancelled by user');
                                if (res.success) {
                                  showToast('合同已取消', 'success');
                                  window.location.reload();
                                } else {
                                  showToast(res.error || '取消失败', 'error');
                                }
                              } catch (error) {
                                showToast('取消失败', 'error');
                              }
                            }
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                        >
                          <Trash2 size={14} className="text-red-500" />
                          <span>取消合同</span>
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Other Party Info */}
          <div className="flex items-center gap-3">
            <span
              className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={(e) => { e.stopPropagation(); navigate(`/profile/${otherParty.user_id}`); }}
            >
            <img
              src={otherParty.avatar}
              alt={otherParty.nickname}
              className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
            />
            <div>
              <p className="text-sm font-medium text-slate-700">{otherParty.nickname}</p>
              <p className="text-xs text-slate-500">{viewMode === 'client' ? '创作者' : '客户'}</p>
            </div>
            </span>
          </div>
        </div>

        {/* Card Body */}
        <div className="p-6 flex-1">
          <p className="text-sm text-slate-600 line-clamp-2 mb-4">{contract.description}</p>

          <div className="grid grid-cols-2 gap-4">
            {/* Amount */}
            <div>
              <p className="text-xs text-slate-500 mb-1">合同金额</p>
              <p className="text-lg font-semibold text-slate-900">
                {formatCurrency(contract.total_amount, contract.currency)}
              </p>
            </div>

            {/* Deadline */}
            <div>
              <p className="text-xs text-slate-500 mb-1">交付日期</p>
              <div className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                <p className={`text-sm font-medium ${isDeadlineOverdue && currentStatus === 'ACTIVE' ? 'text-red-600' : 'text-slate-700'}`}>
                  {currentStatus === 'COMPLETED' || currentStatus === 'CANCELLED'
                    ? formatDate(contract.estimated_delivery_date)
                    : isDeadlineOverdue
                      ? `逾期 ${Math.abs(daysUntilDeadline)} 天`
                      : `${daysUntilDeadline} 天后`}
                </p>
              </div>
            </div>
          </div>

          {/* Warning for overdue active contracts */}
          {currentStatus === 'ACTIVE' && isDeadlineOverdue && (
            <div className="mt-4 flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>此合同已逾期，请尽快完成或联系对方协商延期</span>
            </div>
          )}
        </div>

        {/* Card Footer */}
        <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center justify-between text-xs flex-1">
            <span className="text-slate-500">
              {currentStatus === 'PENDING_CLIENT_APPROVAL' && role === 'client' && '等待你的确认'}
              {currentStatus === 'PENDING_CLIENT_APPROVAL' && role === 'creator' && '等待客户确认'}
              {currentStatus === 'ACTIVE' && `合同执行中`}
              {currentStatus === 'DISPUTED' && `申诉处理中`}
              {currentStatus === 'COMPLETED' && `已于 ${formatDate(contract.completed_at!)} 完成`}
              {currentStatus === 'CANCELLED' && '合同已取消'}
            </span>
            <span className="text-blue-600 font-medium group-hover:underline flex items-center gap-1 group-hover/btn:translate-x-1 transition-transform">
              查看详情 <ArrowRight size={14} />
            </span>
          </div>
        </div>

        {/* Support Link (Bottom Hint) */}
        <div className="absolute -bottom-6 w-full text-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <button
            onClick={(e) => { e.stopPropagation(); showToast('客服热线: 400-123-4567', 'info'); }}
            className="text-[10px] text-slate-400 hover:text-blue-500 flex items-center justify-center gap-1 mx-auto"
          >
            <MessageSquare size={10} /> 遇到困难？联系人工客服
          </button>
        </div>
      </div>

      <DisputeModal
        isOpen={isDisputeOpen}
        onClose={() => setIsDisputeOpen(false)}
        onSubmit={handleDisputeSubmit}
        contractTitle={contract.title}
      />
    </>
  );
};
